import { Spacing, ExpandableSection } from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { useQuestionnaireDashboardData } from "api/useQuestionnaireQueries";
import {
  DashboardLinearGraph,
  type OpenSingleQuestionnaire,
} from "components/graphs/DashboardLinearGraph";
import { Typography } from "components/typography";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

type QuestionnairesDashboardProps = {
  patientUserId?: string;
  openSingleQuestionnaire: (value: OpenSingleQuestionnaire) => void;
};

export const QuestionnairesDashboard = ({
  patientUserId,
  openSingleQuestionnaire,
}: QuestionnairesDashboardProps) => {
  const isCQGraphsEnabled = useFlag("CLINICAL_QUESTIONNAIRE_GRAPHS");
  const { answers, questionnaires } = useQuestionnaireDashboardData({
    patientUserId,
  });
  const { t } = useTranslation();

  if (!isCQGraphsEnabled || !answers) return null;

  return (
    <>
      <Spacing t={20} />
      <ExpandableSection expanded={true}>
        <ExpandableSection.Toggle>
          <Typography insensitive variant="h4">
            {t("QUESTIONNAIRE_GRAPHS")}
          </Typography>
        </ExpandableSection.Toggle>
        <ExpandableSection.Content>
          <WidgetWrapper>
            {questionnaires.map((questionnaire) => {
              return answers[questionnaire.clinicalId] &&
                questionnaire.clinicalId !== "IKBT" ? ( // By request from Tina - don't show IKBT graph
                <Widget key={questionnaire.clinicalId}>
                  <WidgetTextWrapper>
                    <Typography variant="h4">
                      {questionnaire.clinicalId}
                    </Typography>
                  </WidgetTextWrapper>

                  <DashboardLinearGraph
                    openSingleQuestionnaire={openSingleQuestionnaire}
                    questionnaire={questionnaire}
                    answers={answers[questionnaire.clinicalId]}
                  />
                </Widget>
              ) : null;
            })}
          </WidgetWrapper>
        </ExpandableSection.Content>
      </ExpandableSection>
    </>
  );
};

const WidgetWrapper = styled.div`
  font-size: ${({ theme }) => theme.FONT.SIZE.SMALL};
  margin: 2rem 0;
  padding: 1rem 0 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 2rem;
  grid-auto-rows: auto;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const Widget = styled.div`
  display: block;
  position: relative;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
  border-radius: 0.333rem;
  border: 1px solid ${({ theme }) => theme.COLORS.GRAY_MEDIUM};
`;

const WidgetTextWrapper = styled.div`
  padding: 1.25rem 1rem 1rem;
  text-align: center;
`;
